// Create a new file: src/components/AdminTimeOffCreation.js
import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Box,
    Alert,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Select,
    Typography,
    FormHelperText,
    CircularProgress,
    Autocomplete
} from "@mui/material";
import { addDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";

// Use the same leave types as in TimeOffRequest.js
const LEAVE_TYPES = [
    { value: "Annual", label: "Annual Leave / Vacation" },
    { value: "Sick", label: "Sick Leave" },
    { value: "Personal", label: "Personal Leave" },
    { value: "Bereavement", label: "Bereavement Leave" },
    { value: "Parental", label: "Parental Leave" },
    { value: "Maternity", label: "Maternity Leave" },
    { value: "Paternity", label: "Paternity Leave" },
    { value: "Jury", label: "Jury Duty" },
    { value: "Military", label: "Military Leave" },
    { value: "Unpaid", label: "Unpaid Leave" },
    { value: "Training", label: "Training / Professional Development" },
    { value: "Other", label: "Other" }
];

const AdminTimeOffCreation = ({ orgId }) => {
    const [formData, setFormData] = useState({
        startDate: "",
        endDate: "",
        type: "Annual",
        reason: "",
        status: "approved" // Default to approved since admin is creating it
    });

    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    // Fetch employees
    useEffect(() => {
        const fetchEmployees = async () => {
            if (!orgId) return;

            setLoadingEmployees(true);
            try {
                const employeesRef = collection(db, "organizations", orgId, "users");
                const q = query(employeesRef, where("role", "==", "employee"));
                const snapshot = await getDocs(q);

                const employeesList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    displayName: doc.data().displayName || doc.data().email || doc.id
                }));

                setEmployees(employeesList);
            } catch (error) {
                console.error("Error fetching employees:", error);
                setMessage("Failed to load employees. Please try again.");
            } finally {
                setLoadingEmployees(false);
            }
        };

        fetchEmployees();
    }, [orgId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear field-specific error when user makes a change
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Check required fields
        if (!formData.startDate) newErrors.startDate = "Start date is required";
        if (!formData.endDate) newErrors.endDate = "End date is required";
        if (!formData.type) newErrors.type = "Leave type is required";
        if (!selectedEmployee) newErrors.employee = "Employee is required";

        // Validate dates
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);

        if (endDate < startDate) {
            newErrors.endDate = "End date must be after start date";
        }

        // If selecting "Other" type, reason is required
        if (formData.type === "Other" && !formData.reason.trim()) {
            newErrors.reason = "Please provide a reason for 'Other' leave type";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const calculateDuration = () => {
        if (!formData.startDate || !formData.endDate) return null;

        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);

        // Calculate the difference in days (including both start and end dates)
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        return diffDays;
    };

    const handleSubmit = async () => {
        // Reset message
        setMessage("");

        // Check for employee
        if (!selectedEmployee) {
            setErrors({ ...errors, employee: "Please select an employee" });
            return;
        }

        // Validate form
        if (!validateForm()) {
            setMessage("❌ Please correct the errors in the form.");
            return;
        }

        setLoading(true);

        try {
            const duration = calculateDuration();
            const startDate = new Date(formData.startDate);
            startDate.setHours(0, 0, 0, 0);

            const endDate = new Date(formData.endDate);
            endDate.setHours(0, 0, 0, 0);

            // Get leave type label for display purposes
            const leaveTypeObj = LEAVE_TYPES.find(type => type.value === formData.type);
            const leaveTypeLabel = leaveTypeObj ? leaveTypeObj.label : formData.type;

            const requestData = {
                userId: selectedEmployee.id,
                employeeName: selectedEmployee.displayName,
                employeeEmail: selectedEmployee.email || "",
                startDate,
                endDate,
                type: formData.type,
                typeLabel: leaveTypeLabel,
                reason: formData.reason,
                duration,
                requestDate: new Date(),
                status: formData.status, // Use the selected status
                createdByAdmin: true, // Flag to indicate this was created by an admin
                adminNote: "Created by administrator"
            };

            // Add to Firestore
            await addDoc(collection(db, "organizations", orgId, "timeOffRequests"), requestData);

            // Reset form
            setFormData({
                startDate: "",
                endDate: "",
                type: "Annual",
                reason: "",
                status: "approved"
            });
            setSelectedEmployee(null);

            setMessage(`✅ Time off request for ${duration} day${duration !== 1 ? 's' : ''} created successfully for ${selectedEmployee.displayName}.`);
        } catch (error) {
            console.error("Error submitting time off request:", error);
            setMessage(`❌ Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Calculate and show the duration
    const duration = calculateDuration();

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Create Time Off Request for Employee
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Autocomplete
                        fullWidth
                        options={employees}
                        loading={loadingEmployees}
                        value={selectedEmployee}
                        onChange={(event, newValue) => {
                            setSelectedEmployee(newValue);
                            if (errors.employee) {
                                setErrors({
                                    ...errors,
                                    employee: null
                                });
                            }
                        }}
                        getOptionLabel={(option) => option.displayName || option.email || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Employee"
                                error={!!errors.employee}
                                helperText={errors.employee}
                                required
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="date"
                        name="startDate"
                        label="Start Date"
                        value={formData.startDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required
                        error={!!errors.startDate}
                        helperText={errors.startDate}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="date"
                        name="endDate"
                        label="End Date"
                        value={formData.endDate}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required
                        error={!!errors.endDate}
                        helperText={errors.endDate}
                    />
                </Grid>

                {duration && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Duration: {duration} day{duration !== 1 ? 's' : ''}
                        </Alert>
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth required error={!!errors.type}>
                        <InputLabel>Type of Leave</InputLabel>
                        <Select
                            name="type"
                            value={formData.type}
                            label="Type of Leave"
                            onChange={handleChange}
                        >
                            {LEAVE_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            name="status"
                            value={formData.status}
                            label="Status"
                            onChange={handleChange}
                        >
                            <MenuItem value="approved">Approved</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        name="reason"
                        label="Reason"
                        value={formData.reason}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        placeholder="Please provide details about this time off request"
                        required={formData.type === "Other"}
                        error={!!errors.reason}
                        helperText={errors.reason || (formData.type === "Other" ? "Required for 'Other' leave type" : "")}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading}
                        fullWidth
                        sx={{ py: 1.5 }}
                    >
                        {loading ? <CircularProgress size={24} /> : "Create Time Off Request"}
                    </Button>
                </Grid>

                {message && (
                    <Grid item xs={12}>
                        <Alert
                            severity={message.startsWith("✅") ? "success" : "error"}
                            sx={{ mt: 2 }}
                        >
                            {message}
                        </Alert>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default AdminTimeOffCreation;